import deepmerge from 'deepmerge'
import { isPlainObject } from 'is-plain-object'

const isMergeableObject = (o: any) => Array.isArray(o) || isPlainObject(o)

const defaultArrayMerge = (target: any[], source: any[], options?: deepmerge.Options) => {
    // by default we simple overwrite array properties but we must ensure to
    // clone the array and also run merge on all nested values so its a real copy
    return source.map((s) => (isPlainObject(s) ? merge({}, s, options) : s))
}

export const deepArrayMerge = (target: any[], source: any[], options?: deepmerge.Options) => {
    // by default we simple overwrite array properties but we must ensure to
    // clone the array and also run merge on all nested values so its a real copy
    return source.map((s, index) => {
        const targetValue = index < target.length ? target[index] : null
        return isPlainObject(s)
            ? isPlainObject(targetValue)
                ? merge(targetValue, s, options)
                : merge({}, s, options)
            : s
    })
}

export const entriesArrayMerge = (target: any[], source: any[], options?: deepmerge.Options) => {
    // arrays of entries (e.g. repeatable form groups) are merged by index and keep the target entries
    // beyond the source length, other arrays are simply overwritten
    if (!source.some((s) => isPlainObject(s))) {
        return defaultArrayMerge(target, source, options)
    }

    return [
        ...deepArrayMerge(target, source, options),
        ...target.slice(source.length).map((t) => (isPlainObject(t) ? merge({}, t, options) : t)),
    ]
}

export const pushArrayMerge = (target: any[], source: any[], options?: deepmerge.Options) => {
    return [
        ...source.map((s) => (isPlainObject(s) ? merge({}, s, options) : s)),
        ...target.map((t) => (isPlainObject(t) ? merge({}, t, options) : t)),
    ]
}

export function merge<T>(x: Partial<T>, y: Partial<T>, options?: deepmerge.Options): T
export function merge<T1, T2>(x: Partial<T1>, y: Partial<T2>, options?: deepmerge.Options): T1 & T2

export function merge<T1, T2>(x: Partial<T1>, y: Partial<T2>, options?: deepmerge.Options): T1 & T2 {
    return deepmerge(x, y, { isMergeableObject, arrayMerge: defaultArrayMerge, ...options })
}

merge.all = <T>(objects: Partial<T>[], options?: deepmerge.Options): T => {
    return deepmerge.all<T>(objects, { isMergeableObject, arrayMerge: defaultArrayMerge, ...options })
}
